import { Layout, type LayoutProps } from 'react-admin';
import { useMediaQuery, useTheme } from '@mui/material';
import { CustomAppBar } from './CustomAppBar';
import { CustomMenu } from './CustomMenu';

/**
 * Application shell — "Pattern A" (hybrid-3c.md v3.9): a full-height teal
 * rail flush to the viewport edges, with the app bar absolutely positioned
 * over the content column only (see CustomAppBar). Locks the viewport
 * height and hides overflow on desktop (the rail/content each own their
 * own internal scroll); mobile keeps the document's natural scroll with a
 * fixed-position app bar offset.
 *
 * react-admin gotcha (v3.9): there is NO `.RaSidebar-root` in this
 * react-admin version — the rail-clipping chain to force to `height:100vh`
 * is `.RaSidebar-docked` / `.RaSidebar-paper` / `.RaSidebar-fixed`.
 */
export function CustomLayout(props: LayoutProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Layout
      {...props}
      appBar={CustomAppBar}
      menu={CustomMenu}
      sx={{
        '&': {
          backgroundColor: theme.palette.background.default,
          minWidth: 0,
        },

        ...(!isMobile && {
          height: '100vh',
          overflow: 'hidden',
        }),

        // Anchor for the absolutely positioned AppBar, which is docked over
        // the content column only (see CustomAppBar).
        '& .RaLayout-appFrame': {
          marginTop: 0,
          alignItems: 'flex-start',
          ...(!isMobile && { height: '100%', position: 'relative' }),
        },

        // Desktop: react-admin's Sidebar renders as a docked MUI Drawer —
        //   .RaSidebar-docked > .RaSidebar-paper > .RaSidebar-fixed > CustomMenu's rail Box.
        // There is NO `.RaSidebar-root` in this react-admin version. The
        // Drawer paper ships its own overflow/height, which clips the
        // 100vh rail at the foot (the profile card) — force the chain to
        // full height and visible overflow so the rail reaches the bottom.
        '& .RaSidebar-docked': {
          margin: '0 !important',
          padding: '0 !important',
          ...(!isMobile && { height: '100vh' }),
        },
        '& .RaSidebar-paper': {
          border: 'none',
          backgroundColor: 'transparent',
          margin: '0 !important',
          padding: '0 !important',
          ...(!isMobile && {
            position: 'relative',
            height: '100vh',
            overflow: 'visible',
          }),
        },
        '& .RaSidebar-fixed': {
          margin: '0 !important',
          ...(!isMobile && {
            position: 'relative !important',
            height: '100vh !important',
            overflow: 'visible !important',
          }),
        },

        // Pattern A: no outer padding — the rail is flush to the left/top/
        // bottom edges and the row spans the full viewport height (the
        // AppBar is absolute, so this row starts at y=0).
        '& .RaLayout-contentWithSidebar': {
          display: 'flex',
          flex: 1,
          gap: 0,
          width: '100%',
          ...(!isMobile && {
            padding: 0,
            height: '100vh',
            overflow: 'hidden',
          }),
        },

        '& .RaLayout-content': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,

          ...(isMobile && {
            height: 'calc(100dvh - 48px)',
            paddingTop: '48px',
            paddingBottom: 0,
            overflow: 'hidden',
          }),

          ...(!isMobile && {
            height: '100vh',
            // Reserve the top strip for the absolute AppBar (52px), plus the
            // uniform administrative-app page padding (24px) as the single
            // "air" every screen gets — a border-box frame, so it never
            // overflows the content column.
            paddingTop: `calc(52px + ${theme.spacing(3)})`,
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingBottom: theme.spacing(3),
            boxSizing: 'border-box',
            overflow: 'hidden',
          }),
        },

        // The single vertical scroll region; zero top padding so a screen's
        // sticky SectionHeader pins flush under the app bar.
        '& .RaLayout-content > div': {
          width: '100%',
          backgroundColor: theme.palette.background.default,
          WebkitOverflowScrolling: 'touch',
          paddingTop: 0,

          // Vertical scrolling only: wide blocks (tables, code) own their
          // horizontal overflow inside their card. Hiding overflow-x here is
          // the backstop that keeps a stray wide child from expanding the
          // page past the content column's 24px lateral padding (which is
          // what visually killed the right margin).
          ...(!isMobile && {
            borderRadius: 0,
            boxShadow: 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            maxWidth: '100%',
            height: '100%',
          }),

          ...(isMobile && {
            flex: 1,
            minHeight: 0,
            borderRadius: 0,
            boxShadow: 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            maxWidth: '100%',
          }),
        },

        // A screen root is typically a `Stack` whose children are a sticky
        // SectionHeader plus a content block (often a Card with its own
        // `overflow: hidden` for rounded-corner clipping). Because that
        // child's overflow is not `visible`, its flexbox automatic minimum
        // size is 0 (not content-based) — the column flex layout above then
        // shrinks it to fit the scroll region's viewport instead of letting
        // it reach its natural content height, and its own `overflow:
        // hidden` permanently clips whatever no longer fits. `flexShrink: 0`
        // keeps every direct child at its content-based height so the
        // scroll region above (not its children) is what overflows.
        // `minWidth: 0` / `maxWidth: 100%` complete the width chain: a page
        // block may never claim more than the scroll region's width — wide
        // content scrolls inside its own container instead.
        '& .RaLayout-content > div > *': {
          flexShrink: 0,
          minWidth: 0,
          maxWidth: '100%',
        },
      }}
    />
  );
}

export default CustomLayout;
